import { apiUrl, authFetch } from './client';

/**
 * SSE поверх fetch-стрима: EventSource не умеет заголовок Authorization.
 * Формат сервера: `event: <type>\ndata: <json>\n\n`, комментарии `:` — heartbeat.
 */

export interface SseMessage {
  event: string;
  data: string;
  id?: string;
}

/** Инкрементальный парсер: кормим кусками текста, получаем готовые сообщения. */
export function createSseParser(): (chunk: string) => SseMessage[] {
  let buf = '';
  return (chunk: string) => {
    buf += chunk.replace(/\r\n?/g, '\n');
    const out: SseMessage[] = [];
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = 'message';
      let id: string | undefined;
      const data: string[] = [];
      for (const line of block.split('\n')) {
        if (!line || line.startsWith(':')) continue;
        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        let value = colon === -1 ? '' : line.slice(colon + 1);
        if (value.startsWith(' ')) value = value.slice(1);
        if (field === 'event') event = value;
        else if (field === 'data') data.push(value);
        else if (field === 'id') id = value;
      }
      if (data.length === 0) continue;
      out.push(id === undefined ? { event, data: data.join('\n') } : { event, data: data.join('\n'), id });
    }
    return out;
  };
}

export interface SseOptions {
  /** Путь вместе с /api/v1, например `/api/v1/courier/stream`. */
  path: string;
  onEvent: (type: string, data: unknown) => void;
  onOpen?: () => void;
  /** Ошибка соединения; переподключение произойдёт само. */
  onError?: (status: number | null) => void;
  /** Остановить переподключения при этом статусе (например 401 после неудачного refresh, 403). */
  stopOnStatus?: number[];
  minDelayMs?: number;
  maxDelayMs?: number;
  /**
   * Тишина дольше этого срока — соединение считается мёртвым и открывается заново (сервер шлёт
   * heartbeat каждые 15 с). Нужно на телефоне: после возврата приложения из фона поток может
   * молча зависнуть — без ошибки и без событий.
   */
  idleTimeoutMs?: number;
}

/** Подключиться к SSE с переподключением (экспоненциальная задержка). Возвращает функцию закрытия. */
export function connectSse(opts: SseOptions): () => void {
  const minDelay = opts.minDelayMs ?? 1000;
  const maxDelay = opts.maxDelayMs ?? 30000;
  const stopOn = opts.stopOnStatus ?? [401, 403, 404];
  const idleTimeout = opts.idleTimeoutMs ?? 40000;
  let closed = false;
  let delay = minDelay;
  let ctrl: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Время последних байт открытого потока; null — поток не открыт.
  let lastDataAt: number | null = null;

  const dropIfSilent = (limit: number) => {
    if (lastDataAt !== null && Date.now() - lastDataAt > limit) ctrl?.abort();
  };
  const watchdog = setInterval(() => dropIfSilent(idleTimeout), 5000);
  // В фоне таймеры заморожены: при возврате проверяем сразу и строже — чуть дольше одного heartbeat.
  const onVisible = () => {
    if (document.visibilityState === 'visible') dropIfSilent(idleTimeout / 2);
  };
  const doc = typeof document === 'undefined' ? null : document;
  doc?.addEventListener('visibilitychange', onVisible);

  const schedule = () => {
    if (closed) return;
    timer = setTimeout(run, delay);
    delay = Math.min(maxDelay, delay * 2);
  };

  async function run(): Promise<void> {
    if (closed) return;
    ctrl = new AbortController();
    try {
      const res = await authFetch(
        new Request(apiUrl(opts.path), {
          headers: { Accept: 'text/event-stream' },
          signal: ctrl.signal,
          cache: 'no-store',
        }),
      );
      if (!res.ok || !res.body) {
        opts.onError?.(res.status);
        if (stopOn.includes(res.status)) return;
        schedule();
        return;
      }
      delay = minDelay;
      lastDataAt = Date.now();
      opts.onOpen?.();
      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      const parse = createSseParser();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        lastDataAt = Date.now();
        for (const msg of parse(value)) {
          let data: unknown = msg.data;
          try {
            data = JSON.parse(msg.data);
          } catch {
            /* не JSON — отдаём строку */
          }
          opts.onEvent(msg.event, data);
        }
      }
      lastDataAt = null;
      if (!closed) opts.onError?.(null);
    } catch {
      lastDataAt = null;
      if (closed) return;
      opts.onError?.(null);
    }
    schedule();
  }

  void run();
  return () => {
    closed = true;
    if (timer) clearTimeout(timer);
    clearInterval(watchdog);
    doc?.removeEventListener('visibilitychange', onVisible);
    ctrl?.abort();
  };
}
